import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { idpApi } from '../../../../services/api/idpApi';
import { useAlert } from '../../../../contexts/AlertContext';
import { useAuth } from '../../../../contexts/AuthContext';
import useIDPStoreRepository from '../../../../store/idpStoreRepository';

const IDPContext = createContext();

export function useIDP() {
  const context = useContext(IDPContext);
  if (!context) {
    throw new Error('useIDP must be used within an IDPProvider');
  }
  return context;
}

export const IDPProvider = ({ children, goalId, readonly = false }) => {
  const { showAlert } = useAlert();
  const { user } = useAuth();
  const updateGoalInStore = useIDPStoreRepository((state) => state.updateGoal);
  const removeGoalFromStore = useIDPStoreRepository((state) => state.removeGoal);
  const [goalData, setGoalData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Lookup data
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [goalStatuses, setGoalStatuses] = useState([]);
  const [goalStatusTransitions, setGoalStatusTransitions] = useState([]);
  const [ragStatuses, setRagStatuses] = useState([]);
  const [resourceTemplates, setResourceTemplates] = useState([]);

  // Load lookup data
  useEffect(() => {
    const loadLookupData = async () => {
      try {
        const [types, statuses, transitions, rags, templates] = await Promise.all([
          idpApi.getMeasurementTypes(),
          idpApi.getGoalStatuses(),
          idpApi.getGoalStatusTransitions(),
          idpApi.getRAGStatuses(),
          idpApi.getResourceTemplates(),
        ]);
        setMeasurementTypes(types || []);
        setGoalStatuses(statuses || []);
        setGoalStatusTransitions(transitions || []);
        setRagStatuses(rags || []);
        setResourceTemplates(templates || []);
      } catch (err) {
        console.error('Failed to load lookup data:', err);
      }
    };
    loadLookupData();
  }, []);

  // Load goal data
  useEffect(() => {
    const loadGoalData = async () => {
      if (!goalId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await idpApi.getDevelopmentGoalById(goalId);
        setGoalData(data);
        setOriginalData(JSON.parse(JSON.stringify(data)));
        setIsDirty(false);
      } catch (err) {
        setError(err.message || 'Failed to load goal data');
        showAlert('Failed to load development goal', 'error', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadGoalData();
  }, [goalId, showAlert]);

  // Own IDPs are always read-only
  const isOwnGoal = goalData?.employee?.id != null && goalData?.employee?.id === user?.employee?.id;
  const effectiveReadonly = readonly || isOwnGoal;

  // Check if data is dirty (comparing with original)
  const checkDirty = useCallback((newData) => {
    if (!originalData) return true;
    return JSON.stringify(newData) !== JSON.stringify(originalData);
  }, [originalData]);

  // Update general info (title, description, priority, deadline, etc.)
  const updateGeneralInfo = useCallback((updates) => {
    if (effectiveReadonly) return;
    setGoalData(prev => {
      const newData = { ...prev, ...updates };
      setIsDirty(checkDirty(newData));
      return newData;
    });
  }, [effectiveReadonly, checkDirty]);

  // Update actions
  const updateActions = useCallback((actions) => {
    if (effectiveReadonly) return;
    setGoalData(prev => {
      const newData = { ...prev, actions };
      setIsDirty(checkDirty(newData));
      return newData;
    });
  }, [effectiveReadonly, checkDirty]);

  // Update goal-level resources
  const updateResources = useCallback((resources) => {
    if (effectiveReadonly) return;
    setGoalData(prev => {
      const newData = { ...prev, resources };
      setIsDirty(checkDirty(newData));
      return newData;
    });
  }, [effectiveReadonly, checkDirty]);

  // Update goal status
  const updateStatus = useCallback((currentStatusId, statusChangeReason) => {
    if (effectiveReadonly) return;
    setGoalData(prev => {
      const status = goalStatuses.find(s => s.id === currentStatusId);
      const newData = {
        ...prev,
        currentStatusId,
        statusChangeReason,
        currentStatus: status,
      };
      setIsDirty(checkDirty(newData));
      return newData;
    });
  }, [effectiveReadonly, checkDirty, goalStatuses]);

  // Save goal
  const saveGoal = useCallback(async () => {
    if (effectiveReadonly || !goalData) return false;

    try {
      setIsSaving(true);

      // Prepare data for API
      const dataToSave = {
        id: goalData.id,
        employeeId: goalData.employee?.id || goalData.employeeId,
        measurementTypeId: goalData.measurementType?.id || goalData.measurementTypeId,
        title: goalData.title,
        description: goalData.description,
        priority: goalData.priority,
        deadlineAt: goalData.deadlineAt,
        assignedAt: goalData.assignedAt,
        currentStatusId: goalData.currentStatusId || goalData.currentStatus?.id,
        statusChangeReason: goalData.statusChangeReason,
        actions: goalData.actions?.map(action => ({
          id: action.id?.startsWith('temp-') ? null : action.id,
          measurementTypeId: action.measurementType?.id || action.measurementTypeId || goalData.measurementType?.id || goalData.measurementTypeId,
          name: action.name,
          description: action.description,
          measurementDescription: action.measurementDescription,
          deadlineAt: action.deadlineAt,
          priority: action.priority,
          currentProgress: action.currentProgress,
          currentRagStatusId: action.currentRagStatusId || action.currentRagStatus?.id,
          completedAt: action.completedAt,
          statusNotes: action.statusNotes,
          resources: action.resources?.map(resource => ({
            id: resource.id?.startsWith('temp-') ? null : resource.id,
            developmentResourceTemplateId: resource.resourceTemplate?.id || resource.developmentResourceTemplateId,
            name: resource.name,
            details: resource.details,
            usageStatus: resource.usageStatus,
            startedUsingAt: resource.startedUsingAt,
            completedUsingAt: resource.completedUsingAt,
            contributionRating: resource.contributionRating,
            additionalComments: resource.additionalComments,
          })) || [],
        })) || [],
        resources: goalData.resources?.map(resource => ({
          id: resource.id?.startsWith('temp-') ? null : resource.id,
          developmentResourceTemplateId: resource.resourceTemplate?.id || resource.developmentResourceTemplateId,
          name: resource.name,
          details: resource.details,
          usageStatus: resource.usageStatus,
          startedUsingAt: resource.startedUsingAt,
          completedUsingAt: resource.completedUsingAt,
          contributionRating: resource.contributionRating,
          additionalComments: resource.additionalComments,
        })) || [],
      };

      const result = await idpApi.saveDevelopmentGoal(dataToSave);

      if (result) {
        setGoalData(result);
        setOriginalData(JSON.parse(JSON.stringify(result)));
        setIsDirty(false);
        // Update the store so the list view reflects the changes
        updateGoalInStore(result);
        showAlert('Development goal saved successfully', 'success');
        return true;
      }
      return false;
    } catch (err) {
      showAlert(err.message || 'Failed to save development goal', 'error', err);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [effectiveReadonly, goalData, showAlert, updateGoalInStore]);

  // Delete goal
  const deleteGoal = useCallback(async () => {
    if (!goalData?.id) return false;

    try {
      await idpApi.deleteDevelopmentGoal(goalData.id);
      // Remove from the store so the list view reflects the deletion
      removeGoalFromStore(goalData.id);
      showAlert('Development goal deleted successfully', 'success');
      return true;
    } catch (err) {
      showAlert(err.message || 'Failed to delete development goal', 'error', err);
      return false;
    }
  }, [goalData?.id, showAlert, removeGoalFromStore]);

  const contextValue = {
    goalData,
    isDirty,
    isLoading,
    isSaving,
    error,
    readonly: effectiveReadonly,
    // Lookup data
    measurementTypes,
    goalStatuses,
    goalStatusTransitions,
    ragStatuses,
    resourceTemplates,
    // Actions
    updateGeneralInfo,
    updateActions,
    updateResources,
    updateStatus,
    saveGoal,
    deleteGoal,
  };

  return (
    <IDPContext.Provider value={contextValue}>
      {children}
    </IDPContext.Provider>
  );
};

export default IDPContext;
